import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useIsOnline, usePostApi } from "src/hooks";
import { ISection } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setSectionProperty } from "src/redux/slices/generatorSlice";
import { insertValues } from "src/redux/utils/insertValues";
import { deepClone, getYachtId } from "src/utils";

interface Props {
  source: "auto" | "manual" | undefined;
  sectionIndex: number | undefined;
  defaultValue: any | undefined;
  name: string | undefined;
  id: string;
}
/**
 * Custom hook to handle automated NMEA value changes and update the corresponding section.
 *
 * @param props - An object containing the following properties:
 *   - source: A string indicating the source of the value ("auto" or "manual").
 *   - sectionIndex: The index of the section being updated.
 *   - defaultValue: The default value of the field (can be a string, number, or IUnitValue object).
 *   - name: The name of the field being updated.
 *   - id: The unique identifier of the field being updated.
 * @returns An object containing the `handleNMEAChange` function to process value changes.
 */
const useNMEAAutomated = ({
  source,
  sectionIndex,
  id,
  defaultValue = "",
  name = "",
}: Props) => {
  const dispatch = useDispatch();
  const { post } = usePostApi();
  const isOnline = useIsOnline();
  const { getValues } = useFormContext();
  const { lbeidOrPartId, logbookId } = useParams();

  const { sections } = useSelector(s => s.generatorSlice.lbentry);

  const parseValue = (value: any) => {
    if (typeof value === "object") {
      if ("unit" in value) return `${value.value ?? ""} ${value.unit ?? ""}`;
      if ("time" in value) return `${value.time ?? ""} ${value.type ?? ""}`;
    }
    return value;
  };
  /**
   * Handles changes to the NMEA value, updates the section, and logs the change.
   *
   * @param value - The new value to set (can be a string or IUnitValue object).
   */
  const handleNMEAChange = (value: any) => {
    const newValue = parseValue(value);
    const oldValue = parseValue(defaultValue);
    if (
      newValue === oldValue ||
      !source ||
      source === "manual" ||
      sectionIndex === undefined
    )
      return;
    const section: ISection = deepClone(sections[sectionIndex!]);

    const [filledSection] = insertValues(getValues(), [section] as any);
    const newRows = filledSection.rows.map(row => {
      if (!row.cols || !Object.keys(row.cols).includes(id)) return row;
      const clonedCols = deepClone(row.cols);
      clonedCols[id].source = "manual";
      return { ...row, cols: clonedCols };
    });
    dispatch(setSectionProperty({ sectionIndex, property: { rows: newRows } }));
    if (!isOnline) return;
    void post({
      request: "record-history-action",
      body: {
        params: {
          yachtid: getYachtId(),
          lbid: logbookId,
          lbeid: lbeidOrPartId,
          action: `User changed automated value ${oldValue} to manual value ${newValue} for ${name?.replaceAll("<br>", " ") ?? ""}`,
        },
      },
      shouldRunAfterAutosave: true,
    });
  };
  return { handleNMEAChange };
};
export default useNMEAAutomated;
